import { useMemo, useState, useCallback } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { Search, ChevronDown, X, DollarSign, Clock } from "lucide-react";

export interface OutstandingFiltersState {
  search: string;
  vendors: string[];
  dueWithinDays: number | null;
  showEarlyPayOnly: boolean;
}

interface OutstandingFiltersProps {
  vendors: string[];
  earlyPayCount: number;
  filters: OutstandingFiltersState;
  onChange: (filters: OutstandingFiltersState) => void;
}

const DUE_PRESETS = [
  { label: "7 days", value: 7 },
  { label: "14 days", value: 14 },
  { label: "30 days", value: 30 },
  { label: "All", value: null },
] as const;

export function OutstandingFilters({
  vendors,
  earlyPayCount,
  filters,
  onChange,
}: OutstandingFiltersProps) {
  const [vendorOpen, setVendorOpen] = useState(false);
  const [vendorSearch, setVendorSearch] = useState("");

  const update = useCallback(
    (patch: Partial<OutstandingFiltersState>) => onChange({ ...filters, ...patch }),
    [filters, onChange]
  );

  const filteredVendors = useMemo(() => {
    const q = vendorSearch.toLowerCase();
    return q ? vendors.filter((v) => v.toLowerCase().includes(q)) : vendors;
  }, [vendors, vendorSearch]);

  const toggleVendor = (v: string) => {
    const next = filters.vendors.includes(v)
      ? filters.vendors.filter((x) => x !== v)
      : [...filters.vendors, v];
    update({ vendors: next });
  };

  const activeCount =
    (filters.search ? 1 : 0) +
    filters.vendors.length +
    (filters.dueWithinDays != null ? 1 : 0) +
    (filters.showEarlyPayOnly ? 1 : 0);

  return (
    <div className="flex items-center gap-2 flex-wrap">
      {/* Search */}
      <div className="relative flex-1 min-w-[180px] max-w-[260px]">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
        <Input
          value={filters.search}
          onChange={(e) => update({ search: e.target.value })}
          placeholder="Search vendor or invoice #…"
          className="h-7 pl-7 pr-7 text-[11px]"
        />
        {filters.search && (
          <button
            onClick={() => update({ search: "" })}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            <X className="w-3 h-3" />
          </button>
        )}
      </div>

      {/* Vendor dropdown */}
      <Popover open={vendorOpen} onOpenChange={setVendorOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className={cn(
              "h-7 text-[11px] gap-1 px-2.5",
              filters.vendors.length > 0 && "border-primary/40 bg-primary/5 text-primary"
            )}
          >
            Vendor
            {filters.vendors.length > 0 && (
              <Badge variant="secondary" className="h-4 px-1 text-[9px] ml-0.5">
                {filters.vendors.length}
              </Badge>
            )}
            <ChevronDown className="w-3 h-3" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-56 p-2" align="start">
          <Input
            value={vendorSearch}
            onChange={(e) => setVendorSearch(e.target.value)}
            placeholder="Filter vendors…"
            className="h-7 text-[11px] mb-2"
          />
          <div className="max-h-48 overflow-y-auto space-y-0.5">
            {filteredVendors.length === 0 && (
              <p className="text-[10px] text-muted-foreground text-center py-2">No vendors found</p>
            )}
            {filteredVendors.map((v) => (
              <button
                key={v}
                onClick={() => toggleVendor(v)}
                className={cn(
                  "w-full text-left px-2 py-1 rounded text-[11px] transition-colors",
                  filters.vendors.includes(v)
                    ? "bg-primary/10 text-primary font-medium"
                    : "hover:bg-accent text-foreground"
                )}
              >
                {v}
              </button>
            ))}
          </div>
          {filters.vendors.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="w-full h-6 text-[10px] mt-1"
              onClick={() => update({ vendors: [] })}
            >
              Clear selection
            </Button>
          )}
        </PopoverContent>
      </Popover>

      {/* Due within */}
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className={cn(
              "h-7 text-[11px] gap-1 px-2.5",
              filters.dueWithinDays != null && "border-primary/40 bg-primary/5 text-primary"
            )}
          >
            <Clock className="w-3 h-3" />
            {filters.dueWithinDays != null ? `Due ≤ ${filters.dueWithinDays}d` : "Due date"}
            <ChevronDown className="w-3 h-3" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-40 p-2" align="start">
          <div className="space-y-0.5">
            {DUE_PRESETS.map((p) => (
              <button
                key={p.label}
                onClick={() => update({ dueWithinDays: p.value })}
                className={cn(
                  "w-full text-left px-2 py-1 rounded text-[11px] transition-colors",
                  filters.dueWithinDays === p.value
                    ? "bg-primary/10 text-primary font-medium"
                    : "hover:bg-accent text-foreground"
                )}
              >
                {p.label}
              </button>
            ))}
          </div>
        </PopoverContent>
      </Popover>

      {/* Early pay toggle */}
      <button
        onClick={() => update({ showEarlyPayOnly: !filters.showEarlyPayOnly })}
        className={cn(
          "inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md text-[11px] font-medium border transition-colors",
          filters.showEarlyPayOnly
            ? "bg-success text-success-foreground border-success"
            : "bg-background text-muted-foreground border-input hover:bg-accent hover:text-accent-foreground"
        )}
      >
        <DollarSign className="w-3 h-3" />
        Early Pay ({earlyPayCount})
      </button>

      {/* Clear all */}
      {activeCount > 1 && (
        <Button
          variant="ghost"
          size="sm"
          className="h-7 text-[10px] text-muted-foreground px-2"
          onClick={() =>
            onChange({ search: "", vendors: [], dueWithinDays: null, showEarlyPayOnly: false })
          }
        >
          <X className="w-3 h-3 mr-0.5" />
          Clear all
        </Button>
      )}
    </div>
  );
}
